import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { apiPost, getSelectedOrgId, type OrgMembership } from "../api";
import {
  useGetMyApiKeysQuery,
  useRevokeApiKeyMutation,
} from "../store/me";
import { Icons } from "./icons";

interface ApiKey {
  id: string;
  key_prefix: string;
  name: string;
  org_id: string | null;
  roles: string[];
  created_by: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  inserted_at: string;
}

interface CreateResponse {
  api_key: ApiKey;
  plaintext: string;
  warning: string;
}

interface ApiKeysSectionUser {
  roles: string[];
  available_orgs?: OrgMembership[];
  is_platform_admin?: boolean;
  org_id?: string | null;
}

export function ApiKeysSection({ user }: { user: ApiKeysSectionUser }) {
  const canManage = user.roles.includes("admin") || user.roles.includes("operator");
  // Mostramos el dropdown siempre que el user tenga al menos 1 org disponible
  // (antes era >1). Para admins con varias orgs es crítico ver a cuál se
  // está creando la key; para users con 1 sola es redundante pero queda explícito
  // — preferimos claridad sobre brevedad.
  const showOrgPicker =
    (user.available_orgs?.length ?? 0) >= 1 ||
    (user.is_platform_admin === true && getSelectedOrgId() == null);
  // Lookup org_id -> {name, slug} para mostrar en la tabla qué org tiene cada key.
  const orgsById = new Map(
    (user.available_orgs ?? []).map((o) => [o.org_id, { name: o.name, slug: o.slug }])
  );
  // Default org del topbar para preseleccionar en el modal de creación.
  const defaultOrgId = getSelectedOrgId() ?? user.available_orgs?.[0]?.org_id ?? "";
  // RTK Query — cache compartido + tag invalidation al revocar/crear.
  const { data: keys = [], isLoading: loading, refetch: load } = useGetMyApiKeysQuery();
  const [revokeApiKey] = useRevokeApiKeyMutation();
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState<CreateResponse | null>(null);

  async function onRevoke(key: ApiKey) {
    if (!confirm(`¿Revocar "${key.name}"?\n\nTodas las integraciones que la usan van a empezar a recibir 401 inmediatamente. No es reversible.`)) {
      return;
    }
    try {
      await revokeApiKey(key.id).unwrap();
    } catch (err) {
      alert("No se pudo revocar: " + (err instanceof Error ? err.message : "error"));
    }
  }

  return (
    <section className="settings-card">
      <header>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, width: "100%" }}>
          <div>
            <h3>API Keys</h3>
            <p>
              Tokens de larga duración para integraciones server-to-server. Se usan con el header{" "}
              <code className="cfg-code">X-Api-Key</code> en lugar de tener que loguearte.
              Heredan tus roles ({user.roles.join(", ") || "—"}) y tu organización.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
            >
              <Icons.Plus size={13} sw={2.2} />
              <span style={{ marginLeft: 6 }}>Nueva</span>
            </button>
          )}
        </div>
      </header>

      <div className="settings-card-body">
        {revealed && <RevealedKey response={revealed} onClose={() => setRevealed(null)} />}

        {loading ? (
          <div style={{ color: "var(--fg-3)", padding: "12px 0", fontSize: 13 }}>Cargando…</div>
        ) : keys.length === 0 ? (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              background: "var(--bg-2)",
              border: "1px dashed var(--border-1)",
              borderRadius: 6,
              color: "var(--fg-3)",
              fontSize: 13,
            }}
          >
            {canManage
              ? 'Sin API keys todavía. Creá una con el botón "Nueva".'
              : "Sin API keys. Pedile a un admin que cree una."}
          </div>
        ) : (
          <table className="table" style={{ marginTop: 4 }}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Organización</th>
                <th>Prefijo</th>
                <th>Estado</th>
                <th>Último uso</th>
                <th>Creada</th>
                {canManage && <th style={{ width: 80 }}></th>}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td>{orgCell(k.org_id, orgsById)}</td>
                  <td className="mono" style={{ color: "var(--fg-2)" }}>{k.key_prefix}</td>
                  <td>{statusBadge(k)}</td>
                  <td className="text-muted">{relativeTime(k.last_used_at) || "—"}</td>
                  <td className="text-muted">{relativeTime(k.inserted_at)}</td>
                  {canManage && (
                    <td>
                      {!k.revoked_at && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => onRevoke(k)}
                          style={{ color: "var(--st-major)" }}
                        >
                          Revocar
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(res) => {
            setShowCreate(false);
            setRevealed(res);
            load();
          }}
          showOrgPicker={showOrgPicker}
          orgs={user.available_orgs ?? []}
          defaultOrgId={defaultOrgId}
        />
      )}
    </section>
  );
}

/**
 * Renderiza la celda de organización para una API key en la tabla.
 * Caso 1: hay match en available_orgs → "Nombre (slug)"
 * Caso 2: key tiene org_id pero el user no es miembro de esa org → UUID truncado
 * Caso 3: key sin org_id (heredada del creator user) → "—"
 */
function orgCell(
  orgId: string | null,
  orgsById: Map<string, { name: string; slug: string }>
) {
  if (!orgId) return <span className="text-muted">—</span>;
  const match = orgsById.get(orgId);
  if (match) {
    return (
      <span>
        {match.name}{" "}
        <span className="text-muted" style={{ fontSize: 11.5 }}>({match.slug})</span>
      </span>
    );
  }
  // Caso platform_admin viendo una key de otra org a la que no pertenece
  return (
    <span className="mono text-muted" style={{ fontSize: 11.5 }} title={orgId}>
      {orgId.slice(0, 8)}…
    </span>
  );
}

function statusBadge(k: ApiKey) {
  if (k.revoked_at) return <span className="badge badge-muted">revocada</span>;
  if (k.expires_at && new Date(k.expires_at).getTime() < Date.now()) {
    return <span className="badge badge-major">expirada</span>;
  }
  return <span className="badge badge-ok">activa</span>;
}

function relativeTime(iso: string | null): string | null {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `hace ${Math.floor(diff)}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86_400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

function CreateModal({
  onClose,
  onCreated,
  showOrgPicker = false,
  orgs = [],
  defaultOrgId = "",
}: {
  onClose: () => void;
  onCreated: (res: CreateResponse) => void;
  showOrgPicker?: boolean;
  orgs?: OrgMembership[];
  defaultOrgId?: string;
}) {
  const [name, setName] = useState("");
  // Preseleccionamos la org actual (del topbar) o la primera disponible.
  // Si el user tiene varias orgs y no hay default, queda vacío y el usuario
  // tiene que elegir manualmente (el submit valida que esté seteada).
  const [orgId, setOrgId] = useState(defaultOrgId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (showOrgPicker && !orgId) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, string> = { name: name.trim() };
      if (showOrgPicker && orgId) body.org_id = orgId;
      const res = await apiPost<CreateResponse>("/v1/me/api-keys", body);
      onCreated(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div onClick={onClose} className="modal-backdrop">
      <div
        onClick={(e) => e.stopPropagation()}
        className="modal-card"
        style={{ width: 540, maxWidth: "100%", display: "grid", gap: 14, padding: 20 }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}>
              Nueva API key
            </h2>
            <p className="text-sm text-muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Token de larga duración que reemplaza el login. Se manda como{" "}
              <code className="cfg-code">X-Api-Key</code> en cada request.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={onClose}
            disabled={submitting}
            aria-label="Cerrar"
          >
            <Icons.Close size={14} />
          </button>
        </header>
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
          {showOrgPicker && (
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>Organización</span>
              <select
                className="input"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                required
              >
                <option value="" disabled>Elegí una organización…</option>
                {orgs.map((o) => (
                  <option key={o.org_id} value={o.org_id}>{o.name}</option>
                ))}
              </select>
            </label>
          )}
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>Nombre</span>
            <input
              className="input"
              autoFocus
              placeholder='ej: "deploy producción", "ci/cd", "laptop daniel"'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <span className="hint">
              Para que después puedas identificarla en la lista (y revocarla si filtras).
            </span>
          </label>
          {error && (
            <div
              role="alert"
              style={{
                background: "color-mix(in oklab, var(--st-major) 12%, transparent)",
                border: "1px solid color-mix(in oklab, var(--st-major) 35%, transparent)",
                color: "var(--st-major)",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!name.trim() || submitting || (showOrgPicker && !orgId)}
            >
              {submitting ? "Creando…" : "Crear API key"}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function RevealedKey({ response, onClose }: { response: CreateResponse; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(response.plaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      style={{
        background: "color-mix(in oklab, var(--st-degraded) 10%, transparent)",
        border: "1px solid color-mix(in oklab, var(--st-degraded) 35%, transparent)",
        padding: 14,
        borderRadius: 8,
        marginBottom: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>⚠ Copialo ahora — no vas a poder verlo de nuevo</strong>
        <button type="button" className="btn btn-icon btn-ghost btn-sm" onClick={onClose}>
          <Icons.Close size={12} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          className="input text-mono"
          value={response.plaintext}
          readOnly
          style={{ flex: 1, fontSize: 12.5 }}
        />
        <button type="button" className="btn btn-primary btn-sm" onClick={copy}>
          {copied ? "✓ Copiado" : "Copiar"}
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 8 }}>{response.warning}</div>
    </div>
  );
}
